
import { useState, useEffect, useRef } from 'react';
import { products, Product } from '../../mocks/products';
import { tools } from '../../mocks/tools';

type Category = 'all' | 'strategic' | 'foundation' | 'marketing' | 'fishery';

const categoryConfig = {
  strategic: { name: '战略引擎', color: '#FF6B35', icon: 'ri-rocket-2-line', level: 0 },
  foundation: { name: '基础能力', color: '#00D4FF', icon: 'ri-cpu-line', level: 1 },
  marketing: { name: '品牌营销', color: '#A855F7', icon: 'ri-megaphone-line', level: 2 },
  fishery: { name: '渔业领域', color: '#10B981', icon: 'ri-water-flash-line', level: 3 },
};

// 产品协同关系定义
const productRelations = [
  { from: 'auaa', to: 'second-brain', label: '知识赋能' },
  { from: 'auaa', to: 'leo', label: 'AI驱动' },
  { from: 'auaa', to: 'guiyu', label: '商业落地' },
  { from: 'second-brain', to: 'customer-service', label: '智能应答' },
  { from: 'second-brain', to: 'salesmind', label: '销售洞察' },
  { from: 'leo', to: 'fishview', label: '数据分析' },
  { from: 'leo', to: 'aic', label: '智能决策' },
  { from: 'traffic', to: 'official', label: '流量导入' },
  { from: 'traffic', to: 'guiyu', label: '获客转化' },
  { from: 'customer-service', to: 'salesmind', label: '线索流转' },
  { from: 'yuce', to: 'aic', label: '规划执行' },
  { from: 'fishview', to: 'aic', label: '数据联动' },
  { from: 'fishview', to: 'vivian', label: '运维触发' },
  { from: 'aic', to: 'water-monitor', label: '监测控制' },
  { from: 'aic', to: 'pdm', label: '预测维护' },
];

// 3D翻转卡片组件
function ProductCard3D({ product, index }: { product: Product; index: number }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const color = categoryConfig[product.category as keyof typeof categoryConfig]?.color || '#00D4FF';

  const handleClick = () => {
    if (product.url) {
      window.open(product.url, '_blank');
    } else if (product.wechatTip) {
      alert('请在微信中搜索小程序访问');
    }
  };

  return (
    <div
      className="group perspective-1000 h-[320px] cursor-pointer"
      onMouseEnter={() => setIsFlipped(true)}
      onMouseLeave={() => setIsFlipped(false)}
      style={{
        animation: `fadeInUp 0.5s ease-out ${index * 0.05}s both`,
      }}
    >
      <div
        className={`relative w-full h-full transition-transform duration-700 transform-style-3d ${isFlipped ? 'rotate-y-180' : ''
          }`}
      >
        {/* 正面 */}
        <div className="absolute inset-0 backface-hidden">
          <div
            className="h-full p-6 rounded-2xl border backdrop-blur-sm bg-white/5 border-white/10 flex flex-col"
            style={{
              boxShadow: `0 0 30px ${color}10`,
            }}
          >
            {/* 顶部状态栏 */}
            <div className="flex items-center justify-between mb-5">
              <div
                className="w-14 h-14 rounded-xl flex items-center justify-center"
                style={{
                  backgroundColor: `${color}20`,
                  boxShadow: `0 0 20px ${color}30`,
                }}
              >
                <i className={`${product.icon} text-3xl`} style={{ color }}></i>
              </div>
              <div className="flex items-center gap-2">
                {product.url ? (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-[#10B981]/20 rounded-full">
                    <span className="w-1.5 h-1.5 bg-[#10B981] rounded-full animate-pulse"></span>
                    <span className="text-xs text-[#10B981]">在线</span>
                  </span>
                ) : product.wechatTip ? (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-[#07C160]/20 rounded-full">
                    <i className="ri-wechat-line text-xs text-[#07C160]"></i>
                    <span className="text-xs text-[#07C160]">小程序</span>
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-[#F59E0B]/20 rounded-full">
                    <span className="w-1.5 h-1.5 bg-[#F59E0B] rounded-full"></span>
                    <span className="text-xs text-[#F59E0B]">研发中</span>
                  </span>
                )}
              </div>
            </div>

            {/* 产品信息 */}
            <h3 className="text-xl font-bold text-white mb-2">{product.name}</h3>
            <p className="text-sm text-white/50 mb-4 italic flex-1">"{product.slogan}"</p>

            {/* 底部提示 */}
            <div className="flex items-center justify-between pt-4 border-t border-white/10">
              <span
                className="text-xs font-medium px-2 py-1 rounded"
                style={{ color, backgroundColor: `${color}15` }}
              >
                {categoryConfig[product.category as keyof typeof categoryConfig]?.name}
              </span>
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <i className="ri-refresh-line"></i>
                <span>悬停查看详情</span>
              </div>
            </div>

            {/* 装饰光效 */}
            <div
              className="absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20"
              style={{ backgroundColor: color }}
            />
          </div>
        </div>

        {/* 背面 */}
        <div className="absolute inset-0 backface-hidden rotate-y-180">
          <div
            className="h-full p-6 rounded-2xl border backdrop-blur-sm flex flex-col"
            style={{
              backgroundColor: `${color}10`,
              borderColor: `${color}40`,
              boxShadow: `0 0 40px ${color}20`,
            }}
          >
            {/* 标题 */}
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${color}30` }}
              >
                <i className={`${product.icon} text-xl`} style={{ color }}></i>
              </div>
              <h3 className="text-lg font-bold text-white">{product.name}</h3>
            </div>

            {/* 核心亮点 */}
            <div className="flex-1">
              <p className="text-xs text-white/50 mb-3 uppercase tracking-wider">核心亮点</p>
              <div className="grid grid-cols-2 gap-2">
                {product.highlights.map((highlight, idx) => (
                  <div
                    key={idx}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ backgroundColor: color }}
                    ></span>
                    <span className="text-xs text-white/80 truncate">{highlight}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 访问按钮 */}
            <button
              onClick={handleClick}
              className="mt-4 w-full py-3 rounded-xl font-medium text-sm transition-all duration-300 flex items-center justify-center gap-2 cursor-pointer whitespace-nowrap"
              style={{
                backgroundColor: color,
                color: '#030712',
              }}
            >
              <span>{product.url || product.wechatTip ? '立即访问' : '敬请期待'}</span>
              <i className="ri-arrow-right-line"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// 产品架构关系图组件
function ArchitectureGraph() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredNode, setHoveredNode] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  // 节点位置计算
  const getNodePositions = () => {
    const width = dimensions.width || 1200;
    const height = dimensions.height || 600;
    const centerX = width / 2;

    const positions: Record<string, { x: number; y: number; product: Product }> = {};

    // 战略层 - 顶部中央
    const strategicProducts = products.filter(p => p.category === 'strategic');
    strategicProducts.forEach((p, i) => {
      positions[p.id] = { x: centerX, y: 80, product: p };
    });

    // 基础能力层 - 第二行
    const foundationProducts = products.filter(p => p.category === 'foundation');
    const foundationSpacing = width / (foundationProducts.length + 1);
    foundationProducts.forEach((p, i) => {
      positions[p.id] = { x: foundationSpacing * (i + 1), y: 200, product: p };
    });

    // 品牌营销层 - 第三行
    const marketingProducts = products.filter(p => p.category === 'marketing');
    const marketingSpacing = width / (marketingProducts.length + 1);
    marketingProducts.forEach((p, i) => {
      positions[p.id] = { x: marketingSpacing * (i + 1), y: 350, product: p };
    });

    // 渔业领域层 - 底部
    const fisheryProducts = products.filter(p => p.category === 'fishery');
    const fisherySpacing = width / (fisheryProducts.length + 1);
    fisheryProducts.forEach((p, i) => {
      positions[p.id] = { x: fisherySpacing * (i + 1), y: 500, product: p };
    });

    return positions;
  };

  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight,
        });
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || dimensions.width === 0) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const positions = getNodePositions();
    let animationFrame: number;
    let time = 0;

    const draw = () => {
      ctx.clearRect(0, 0, dimensions.width, dimensions.height);
      time += 0.02;

      // 绘制连线
      productRelations.forEach((relation, index) => {
        const from = positions[relation.from];
        const to = positions[relation.to];
        if (!from || !to) return;

        const isHighlighted = hoveredNode === relation.from || hoveredNode === relation.to;
        const gradient = ctx.createLinearGradient(from.x, from.y, to.x, to.y);

        const fromColor = categoryConfig[from.product.category as keyof typeof categoryConfig]?.color || '#00D4FF';
        const toColor = categoryConfig[to.product.category as keyof typeof categoryConfig]?.color || '#00D4FF';

        if (isHighlighted) {
          gradient.addColorStop(0, fromColor);
          gradient.addColorStop(1, toColor);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 3;
          ctx.globalAlpha = 1;
        } else {
          gradient.addColorStop(0, `${fromColor}40`);
          gradient.addColorStop(1, `${toColor}40`);
          ctx.strokeStyle = gradient;
          ctx.lineWidth = 1.5;
          ctx.globalAlpha = 0.5;
        }

        // 绘制曲线
        ctx.beginPath();
        const midX = (from.x + to.x) / 2;
        const midY = (from.y + to.y) / 2 - 30;
        ctx.moveTo(from.x, from.y);
        ctx.quadraticCurveTo(midX, midY, to.x, to.y);
        ctx.stroke();

        // 绘制流动粒子
        if (isHighlighted) {
          const t = (Math.sin(time * 2 + index) + 1) / 2;
          const particleX = from.x + (to.x - from.x) * t;
          const particleY = from.y + (to.y - from.y) * t - 30 * Math.sin(Math.PI * t);

          ctx.beginPath();
          ctx.arc(particleX, particleY, 4, 0, Math.PI * 2);
          ctx.fillStyle = '#fff';
          ctx.globalAlpha = 1;
          ctx.fill();
        }

        ctx.globalAlpha = 1;
      });

      animationFrame = requestAnimationFrame(draw);
    };

    draw();
    return () => cancelAnimationFrame(animationFrame);
  }, [dimensions, hoveredNode]);

  const positions = getNodePositions();

  return (
    <div ref={containerRef} className="relative w-full h-[600px]">
      <canvas
        ref={canvasRef}
        width={dimensions.width}
        height={dimensions.height}
        className="absolute inset-0"
      />

      {/* 节点 */}
      {Object.entries(positions).map(([id, pos]) => {
        const color = categoryConfig[pos.product.category as keyof typeof categoryConfig]?.color || '#00D4FF';
        const isHovered = hoveredNode === id;

        return (
          <div
            key={id}
            className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer transition-all duration-300"
            style={{ left: pos.x, top: pos.y }}
            onMouseEnter={() => setHoveredNode(id)}
            onMouseLeave={() => setHoveredNode(null)}
            onClick={() => {
              if (pos.product.url) {
                window.open(pos.product.url, '_blank');
              }
            }}
          >
            <div
              className={`relative flex flex-col items-center transition-all duration-300 ${isHovered ? 'scale-125 z-10' : 'scale-100'
                }`}
            >
              {/* 节点圆圈 */}
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center border-2 transition-all duration-300"
                style={{
                  backgroundColor: isHovered ? color : `${color}30`,
                  borderColor: color,
                  boxShadow: isHovered ? `0 0 30px ${color}80` : `0 0 15px ${color}40`,
                }}
              >
                <i
                  className={`${pos.product.icon} text-xl transition-colors duration-300`}
                  style={{ color: isHovered ? '#030712' : color }}
                ></i>
              </div>

              {/* 产品名称 */}
              <div
                className={`mt-2 px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap transition-all duration-300 ${isHovered ? 'bg-white/20' : 'bg-white/5'
                  }`}
                style={{ color: isHovered ? '#fff' : 'rgba(255,255,255,0.7)' }}
              >
                {pos.product.name.length > 6 ? pos.product.name.slice(0, 6) + '...' : pos.product.name}
              </div>

              {/* 悬停详情卡片 */}
              {isHovered && (
                <div
                  className="absolute top-full mt-4 p-4 rounded-xl border backdrop-blur-xl z-20 w-64"
                  style={{
                    backgroundColor: `${color}20`,
                    borderColor: `${color}50`,
                    boxShadow: `0 10px 40px ${color}30`,
                  }}
                >
                  <h4 className="text-sm font-bold text-white mb-1">{pos.product.name}</h4>
                  <p className="text-xs text-white/60 italic mb-3">"{pos.product.slogan}"</p>
                  <div className="flex flex-wrap gap-1">
                    {pos.product.highlights.slice(0, 4).map((h, i) => (
                      <span key={i} className="text-xs px-2 py-0.5 bg-white/10 rounded text-white/70">
                        {h}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* 层级标签 */}
      <div className="absolute left-4 top-[80px] -translate-y-1/2">
        <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: '#FF6B3520', color: '#FF6B35' }}>
          战略引擎层
        </span>
      </div>
      <div className="absolute left-4 top-[200px] -translate-y-1/2">
        <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: '#00D4FF20', color: '#00D4FF' }}>
          基础能力层
        </span>
      </div>
      <div className="absolute left-4 top-[350px] -translate-y-1/2">
        <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: '#A855F720', color: '#A855F7' }}>
          品牌营销层
        </span>
      </div>
      <div className="absolute left-4 top-[500px] -translate-y-1/2">
        <span className="text-xs font-medium px-3 py-1.5 rounded-full" style={{ backgroundColor: '#10B98120', color: '#10B981' }}>
          渔业领域层
        </span>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [activeCategory, setActiveCategory] = useState<Category>('all');
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'matrix' | 'architecture'>('matrix');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const filteredProducts = products.filter(p => {
    const matchCategory = activeCategory === 'all' || p.category === activeCategory;
    const matchSearch = searchQuery === '' ||
      p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.slogan.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div
      ref={containerRef}
      className="min-h-screen bg-[#030712] text-white overflow-hidden relative"
    >
      {/* 动态光标追踪效果 */}
      <div
        className="pointer-events-none fixed w-[600px] h-[600px] rounded-full opacity-20 blur-3xl transition-all duration-300 ease-out z-0"
        style={{
          background: 'radial-gradient(circle, rgba(0,212,255,0.3) 0%, transparent 70%)',
          left: mousePos.x - 300,
          top: mousePos.y - 300,
        }}
      />

      {/* 背景网格 */}
      <div className="fixed inset-0 z-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `
            linear-gradient(rgba(0,212,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,212,255,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
        }} />
      </div>

      {/* 浮动粒子 */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#00D4FF] rounded-full opacity-40"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
            }}
          />
        ))}
      </div>

      {/* 顶部导航 */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#030712]/80 border-b border-white/5">
        <div className="max-w-[1800px] mx-auto px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img
              src="https://static.readdy.ai/image/9e747d83165d349a03f5427ea4fab807/ff89e372032fff0d5aff6b38fd727991.png"
              alt="陆渔科技"
              className="h-10 w-auto"
            />
            <div className="hidden md:block">
              <span className="text-lg font-bold bg-gradient-to-r from-[#00D4FF] to-[#00FFA3] bg-clip-text text-transparent">
                AI产品矩阵
              </span>
            </div>
          </div>

          {/* 搜索框 */}
          <div className="flex-1 max-w-md mx-8">
            <div className="relative group">
              <input
                type="text"
                placeholder="搜索产品..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-5 py-2.5 bg-white/5 border border-white/10 rounded-full text-sm text-white placeholder-white/40 focus:outline-none focus:border-[#00D4FF]/50 focus:bg-white/10 transition-all duration-300"
              />
              <i className="ri-search-line absolute right-4 top-1/2 -translate-y-1/2 text-white/40 group-focus-within:text-[#00D4FF]"></i>
            </div>
          </div>

          <a
            href="https://luyuaqua.com"
            target="_blank"
            rel="noopener noreferrer"
            className="px-5 py-2 bg-gradient-to-r from-[#00D4FF]/20 to-[#00FFA3]/20 border border-[#00D4FF]/30 rounded-full text-sm font-medium hover:border-[#00D4FF] transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            访问官网
          </a>
        </div>
      </header>

      {/* 主内容区 */}
      <main className="relative z-10 pt-24 pb-20">
        {/* Hero区域 */}
        <section className="px-8 py-16 text-center">
          <div className="max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#00D4FF]/10 border border-[#00D4FF]/20 rounded-full mb-8">
              <span className="w-2 h-2 bg-[#00FFA3] rounded-full animate-pulse"></span>
              <span className="text-sm text-[#00D4FF]">14款AI产品 · 全域覆盖</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <span className="text-white">确定性农业</span>
              <br />
              <span className="bg-gradient-to-r from-[#00D4FF] via-[#00FFA3] to-[#00D4FF] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
                智能产品矩阵
              </span>
            </h1>
            <p className="text-lg text-white/50 max-w-2xl mx-auto">
              以AI重构农业生产确定性，从战略决策到一线执行，全链路智能化赋能
            </p>
          </div>
        </section>

        {/* 视图切换 */}
        <section className="px-8 mb-8">
          <div className="max-w-[1800px] mx-auto flex justify-center">
            <div className="inline-flex p-1 bg-white/5 border border-white/10 rounded-full">
              <button
                onClick={() => setActiveTab('matrix')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${activeTab === 'matrix'
                  ? 'bg-[#00D4FF] text-[#030712]'
                  : 'text-white/60 hover:text-white'
                  }`}
              >
                <i className="ri-grid-fill mr-2"></i>
                产品矩阵
              </button>
              <button
                onClick={() => setActiveTab('architecture')}
                className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${activeTab === 'architecture'
                  ? 'bg-[#00D4FF] text-[#030712]'
                  : 'text-white/60 hover:text-white'
                  }`}
              >
                <i className="ri-mind-map mr-2"></i>
                架构关系图
              </button>
            </div>
          </div>
        </section>

        {activeTab === 'matrix' ? (
          <>
            {/* 分类导航 */}
            <section className="px-8 mb-12">
              <div className="max-w-[1800px] mx-auto">
                <div className="flex flex-wrap items-center justify-center gap-3">
                  <button
                    onClick={() => setActiveCategory('all')}
                    className={`group relative px-6 py-3 rounded-xl border transition-all duration-300 cursor-pointer whitespace-nowrap ${activeCategory === 'all'
                      ? 'bg-white/10 border-white/30 text-white'
                      : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:text-white'
                      }`}
                  >
                    <div className="flex items-center gap-2">
                      <i className="ri-apps-2-line text-lg"></i>
                      <span className="font-medium">全部产品</span>
                      <span className="text-xs opacity-60">({products.length})</span>
                    </div>
                  </button>

                  {Object.entries(categoryConfig).map(([key, config]) => {
                    const count = products.filter(p => p.category === key).length;
                    return (
                      <button
                        key={key}
                        onClick={() => setActiveCategory(key as Category)}
                        className={`group relative px-6 py-3 rounded-xl border transition-all duration-300 cursor-pointer whitespace-nowrap ${activeCategory === key
                          ? 'text-white'
                          : 'bg-white/5 border-white/10 text-white/60 hover:border-white/20 hover:text-white'
                          }`}
                        style={{
                          backgroundColor: activeCategory === key ? `${config.color}20` : undefined,
                          borderColor: activeCategory === key ? `${config.color}50` : undefined,
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <i className={`${config.icon} text-lg`} style={{ color: config.color }}></i>
                          <span className="font-medium">{config.name}</span>
                          <span className="text-xs opacity-60">({count})</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* 产品矩阵 - 3D翻转卡片 */}
            <section className="px-8 mb-20">
              <div className="max-w-[1800px] mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
                  {filteredProducts.map((product, index) => (
                    <ProductCard3D key={product.id} product={product} index={index} />
                  ))}
                </div>
              </div>
            </section>
          </>
        ) : (
          /* 产品架构关系图 */
          <section className="px-8 mb-20">
            <div className="max-w-[1800px] mx-auto">
              <div className="p-6 bg-white/5 border border-white/10 rounded-2xl">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-white mb-1">产品协同架构</h2>
                    <p className="text-sm text-white/40">悬停节点查看产品详情，连线展示数据流转与协同关系</p>
                  </div>
                  <div className="flex items-center gap-4">
                    {Object.entries(categoryConfig).map(([key, config]) => (
                      <div key={key} className="flex items-center gap-2">
                        <span className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }}></span>
                        <span className="text-xs text-white/60">{config.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <ArchitectureGraph />
              </div>
            </div>
          </section>
        )}

        {/* 赋能工具集 */}
        <section className="px-8 mb-20">
          <div className="max-w-[1800px] mx-auto">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-1 h-12 bg-gradient-to-b from-[#00D4FF] to-[#00FFA3] rounded-full"></div>
              <div>
                <h2 className="text-2xl font-bold text-white">赋能工具集</h2>
                <p className="text-sm text-white/40">辅助工具 · 效率提升 · 数据洞察</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {tools.map((tool, index) => (
                <div
                  key={tool.id}
                  className="group relative p-6 bg-white/5 border border-white/10 rounded-2xl hover:bg-white/10 hover:border-[#00D4FF]/30 transition-all duration-300 cursor-pointer"
                  onClick={() => tool.url && window.open(tool.url, '_blank')}
                  style={{
                    animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`,
                  }}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00D4FF]/20 to-[#00FFA3]/20 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${tool.icon} text-2xl text-[#00D4FF]`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-lg font-bold text-white">{tool.name}</h3>
                        <span className="text-xs text-white/40 px-2 py-0.5 bg-white/5 rounded">{tool.type}</span>
                      </div>
                      <p className="text-sm text-white/50 mb-3 italic">"{tool.slogan}"</p>
                      <div className="flex flex-wrap gap-1.5">
                        {tool.highlights.slice(0, 3).map((h, idx) => (
                          <span key={idx} className="text-xs text-white/40 px-2 py-0.5 bg-white/5 rounded">
                            {h}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {tool.url && (
                    <div className="absolute bottom-4 right-4 flex items-center gap-1 text-[#00D4FF] text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span>访问</span>
                      <i className="ri-arrow-right-up-line"></i>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* 底部信息 */}
        <footer className="px-8 border-t border-white/10">
          <div className="max-w-[1800px] mx-auto py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <img
                  src="https://static.readdy.ai/image/9e747d83165d349a03f5427ea4fab807/ff89e372032fff0d5aff6b38fd727991.png"
                  alt="陆渔科技"
                  className="h-8 w-auto opacity-60"
                />
                <div className="text-sm text-white/40">
                  <span>陆渔科技 · 确定性农业开创者</span>
                </div>
              </div>
              <div className="flex items-center gap-6">
                <a href="https://luyuaqua.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 hover:text-[#00D4FF] transition-colors cursor-pointer">
                  官网
                </a>
                <a href="https://auaa.luyuaqua.com" target="_blank" rel="noopener noreferrer" className="text-sm text-white/40 hover:text-[#00D4FF] transition-colors cursor-pointer">
                  确定性农业引擎
                </a>
                <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-white/30 hover:text-[#00D4FF] transition-colors cursor-pointer">
                  Powered by LUYUAQUA
                </a>
              </div>
            </div>
            <div className="mt-8 pt-6 border-t border-white/5 text-center">
              <p className="text-xs text-white/30">© 2025 陆渔科技 All Rights Reserved</p>
            </div>
          </div>
        </footer>
      </main>

      {/* 全局样式 */}
      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes float {
          0%, 100% {
            transform: translateY(0) translateX(0);
          }
          25% {
            transform: translateY(-20px) translateX(10px);
          }
          50% {
            transform: translateY(-10px) translateX(-10px);
          }
          75% {
            transform: translateY(-30px) translateX(5px);
          }
        }

        @keyframes gradient {
          0% {
            background-position: 0% center;
          }
          50% {
            background-position: 100% center;
          }
          100% {
            background-position: 0% center;
          }
        }

        .animate-gradient {
          animation: gradient 4s ease infinite;
        }

        .perspective-1000 {
          perspective: 1000px;
        }

        .transform-style-3d {
          transform-style: preserve-3d;
        }

        .backface-hidden {
          backface-visibility: hidden;
        }

        .rotate-y-180 {
          transform: rotateY(180deg);
        }

        /* 自定义滚动条 */
        ::-webkit-scrollbar {
          width: 6px;
        }
        ::-webkit-scrollbar-track {
          background: transparent;
        }
        ::-webkit-scrollbar-thumb {
          background: rgba(0, 212, 255, 0.3);
          border-radius: 3px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: rgba(0, 212, 255, 0.5);
        }
      `}</style>
    </div>
  );
}
